import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import {
  WorksheetAssociatedTestSection,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import { StabilizedMaterial } from 'Components/Worksheets/Core/Fields/StabilizedMaterial';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import externalTestMethods from 'Components/Worksheets/ExternalTestMethods';
import {
  AggregationsBrokenDownByMethod,
  BrokenDownToPassSieveSizeOption,
  GradningZoneType,
  MouldSize,
  SampleDriedByMethod,
  VisualNominalParticleSizeType,
} from 'Components/Worksheets/MaximumDryDensityWorksheet/types';
import { GrainSize } from 'Components/Worksheets/MoistureContentWorksheet/types';
import { UsedSubSamples } from 'Components/Worksheets/Shared/types';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { useSelector } from 'react-redux';

let BsPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, prefixName }) => {
  const gradingZone = useSelector(pipe(createWorksheetSelector!('Preparation.GradingZone')), deepEquals) as
    | GradningZoneType
    | undefined;

  const visualNominalParticleSize = useSelector(
    createWorksheetSelector!('Preparation.VisualNominalParticleSize'),
    deepEquals
  );

  const isSusceptibleToCrushing = useSelector(
    pipe(createWorksheetSelector!('Preparation.IsSampleSusceptibleToCrushing'), valueOrDefault(false))
  ) as boolean;

  const isOvenDried = useSelector(
    pipe(createWorksheetSelector!('Preparation.SampleDriedBy'), (d) => d === SampleDriedByMethod.Oven)
  );

  const noneAggregations = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.AggregationsBrokenDownBy'),
      (d) => d === AggregationsBrokenDownByMethod.None
    )
  );

  const isGradingAssessmentMeasured = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.AssociatedOversizeDetermination.AssociatedTestType'),
      (t) => t === 'measured'
    )
  );

  const completeTestByAgreementWithClient = useSelector(
    pipe(createWorksheetSelector!('Preparation.CompleteTestByAgreementWithClient'), valueOrDefault(false))
  );

  const gradingZoneEqualsTwo = gradingZone === GradningZoneType.Two;

  const gradingZoneEqualsFourOrFive = gradingZone === GradningZoneType.Four || gradingZone === GradningZoneType.Five;

  const gradingZoneEqualsX = gradingZone === GradningZoneType.X;

  const gradingZoneEqualsXAndClientAgreementNo = gradingZoneEqualsX && !completeTestByAgreementWithClient;

  const gradingZoneEqualsXAndClientAgreementYes = gradingZoneEqualsX && completeTestByAgreementWithClient;

  const mouldSizeIsOneLitre = useSelector(
    pipe(createWorksheetSelector!('Preparation.MouldType'), (t) => t === MouldSize.OneLitre)
  );

  const mouldSizeIsCBR = useSelector(
    pipe(createWorksheetSelector!('Preparation.MouldType'), (t) => t === MouldSize.CBR)
  );

  const isSampleSusceptibleToCrushingOptions: Array<{ label: string; value: boolean }> = [
    { label: 'Susceptible', value: true },
    { label: 'Not Susceptible', value: false },
  ];

  const usedSubSamplesOptions: Array<{ label: string; value: string }> = [
    { label: 'Single sub-sample', value: UsedSubSamples.Single },
    { label: 'Multiple sub-samples', value: UsedSubSamples.Multiple },
  ];
  const brokenDownToPassSieveSize = useSelector(
    pipe(createWorksheetSelector!('Preparation.BrokenDownToPassSieveSize'))
  );

  const isCopyGrainSizeChecked = useSelector(
    pipe(createWorksheetSelector!('Preparation.CopyGrainSize'), valueOrDefault(false))
  ) as boolean;

  const waterContentTestIsUnnecessary = useSelector(
    pipe(
      createWorksheetSelector!(
        'Preparation.AssociatedOversizeDetermination.MeasuredTest.WaterContentTestIsUnnecessary'
      ),
      valueOrDefault(false)
    )
  );

  let visualNominalParticleSizeSieveOptions = [
    { label: '63.0 µm', value: VisualNominalParticleSizeType.Sieve0_063mm },
    { label: '2.00 mm', value: VisualNominalParticleSizeType.Sieve2_00mm },
    { label: '10.0 mm', value: VisualNominalParticleSizeType.Sieve10_0mm },
    {
      label: '31.5 mm',
      value: VisualNominalParticleSizeType.Sieve31_5mm,
      disabled: !(
        (gradingZone === GradningZoneType.Two && mouldSizeIsCBR) ||
        gradingZone === GradningZoneType.Three ||
        gradingZone === GradningZoneType.Four ||
        gradingZone === GradningZoneType.Five ||
        gradingZoneEqualsXAndClientAgreementYes
      ),
    },
    {
      label: '63.0 mm',
      value: VisualNominalParticleSizeType.Sieve63_0mm,
      disabled: !(
        gradingZone === GradningZoneType.Four ||
        gradingZone === GradningZoneType.Five ||
        gradingZoneEqualsXAndClientAgreementYes
      ),
    },
  ];

  const showCopyGrainSize = isGradingAssessmentMeasured && !waterContentTestIsUnnecessary;

  const grainSize = useSelector(pipe(createWorksheetSelector!('Preparation.FineGrainSize'))) as GrainSize;
  const grainSizeOptions: Array<{ label: string; value: GrainSize }> = [
    { label: 'Fine', value: GrainSize.Fine },
    { label: 'Coarse', value: GrainSize.Coarse },
  ];

  const brokenDownToPassSieveSizeOptions = [
    { label: '5', value: BrokenDownToPassSieveSizeOption.Sieve5mm },
    { label: '20', value: BrokenDownToPassSieveSizeOption.Sieve20mm },
  ];

  const importedPsdTestId = useSelector(
    createWorksheetSelector!('Preparation.AssociatedOversizeDetermination.ImportedTest.Id')
  );

  return (
    <>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="Preparation.IsSampleSusceptibleToCrushing"
          options={isSampleSusceptibleToCrushingOptions}
        />

        {isSusceptibleToCrushing ? (
          <Field input={<TextOutput />} name="Preparation.UsedSubSamples" value={'Multiple sub-samples'} />
        ) : (
          <Field input={<RadioGroup />} name="Preparation.UsedSubSamples" options={usedSubSamplesOptions} />
        )}

        <StabilizedMaterial editable appendNamePrefix="Preparation" />

        <NamePrefixModifier appendNamePrefix="Equipment">
          <WorksheetBalanceFieldSet name="TareMassBalance" label="Balance" />
        </NamePrefixModifier>

        <Field input={<NumericInput />} name="Preparation.TareMass" />

        <Field input={<NumericInput />} name="Preparation.TareAndTotalSampleMass" />
        <Field input={<TextOutput />} name="Preparation.TotalSampleMass" />
      </ListFieldSection>

      <WorksheetAssociatedTestSection
        label="Grading Assessment"
        heading="Grading Assessment"
        name="Preparation.AssociatedOversizeDetermination"
        testType="Sieve Analysis"
        optionsLabels={{
          external: 'External (results only)',
        }}
        associatedTestModel={{
          measured: true,
          imported: {
            importedTestResults: (
              <ListFieldSection>
                <Field
                  input={<TextOutput />}
                  name={`Preparation.OversizeDeterminationResults.Sieves[${0}].CumulativeDryMassPassingPercent`}
                />
                <Field
                  input={<TextOutput />}
                  name={`Preparation.OversizeDeterminationResults.Sieves[${1}].CumulativeDryMassPassingPercent`}
                />
                <Field
                  input={<TextOutput />}
                  name={`Preparation.OversizeDeterminationResults.Sieves[${0}].CumulativeDryMassRetainedPercent`}
                />
                <Field
                  input={<TextOutput />}
                  name={`Preparation.OversizeDeterminationResults.Sieves[${1}].CumulativeDryMassRetainedPercent`}
                />
              </ListFieldSection>
            ),
            importModel: {
              Id: importedPsdTestId,
            },
          },
          external: {
            testMethods: [externalTestMethods.psd.BS_EN_ISO_17892_4],
            permitOtherTestMethods: false,
            externalTestResults: (
              <ListFieldSection>
                <Field
                  input={<NumericInput />}
                  name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${0}].CumulativeDryMassPassingPercent`}
                />
                <Field
                  input={<NumericInput />}
                  name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${1}].CumulativeDryMassPassingPercent`}
                />
                <Field
                  input={<TextOutput />}
                  name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${0}].CumulativeDryMassRetainedPercent`}
                />
                <Field
                  input={<TextOutput />}
                  name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${1}].CumulativeDryMassRetainedPercent`}
                />
              </ListFieldSection>
            ),
          },
        }}
      />

      <ListFieldSection>
        {showCopyGrainSize && (
          <>
            <Field
              input={<TextOutput />}
              name={`Preparation.AssociatedOversizeDetermination.MeasuredTest.Sieves[${1}].DryMassPassing`}
            />
            <Field
              input={<TextOutput />}
              name={`Preparation.AssociatedOversizeDetermination.MeasuredTest.TotalMaterialDryMass`}
            />
          </>
        )}
        <Field input={<TextOutput />} name="Preparation.GradingZone" />

        {gradingZoneEqualsTwo && (
          <Field
            input={<RadioGroup />}
            name="Preparation.MouldType"
            options={[
              { label: '1 Litre', value: MouldSize.OneLitre },
              { label: 'CBR', value: MouldSize.CBR },
            ]}
          />
        )}

        {gradingZoneEqualsX && (
          <Field
            input={<RadioGroup />}
            name="Preparation.CompleteTestByAgreementWithClient"
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            confirm={completeTestByAgreementWithClient}
            confirmText="This will clear the remainder of the worksheet and report the result as not obtainable. This cannot be undone. Continue?"
          />
        )}

        {gradingZoneEqualsXAndClientAgreementYes && (
          <Field
            input={<RadioGroup />}
            name="Preparation.MaterialLargerThan37_5mm"
            options={[{ label: 'Replaced by 20mm to 37.5mm Material', value: 'Replaced by 20mm to 37.5mm Material' }]}
            freeEntry={true}
            freeEntryLabel="Other"
            freeEntryMaxLength={100}
          />
        )}

        {!gradingZoneEqualsXAndClientAgreementNo && (
          <>
            <Field
              input={<RadioGroup />}
              name="Preparation.SampleDriedBy"
              options={[
                { label: 'Natural/None', value: 'None' },
                { label: 'Air Dried', value: 'Air' },
                { label: 'Oven Dried at 50°C', value: 'Oven' },
              ]}
            />

            {isOvenDried && (
              <NamePrefixModifier appendNamePrefix="Equipment">
                <Field
                  input={<WorksheetEquipmentDropdown />}
                  label="Oven"
                  equipmentType="oven"
                  name="PreparationOven"
                />
              </NamePrefixModifier>
            )}

            <Field
              input={<RadioGroup />}
              name="Preparation.AggregationsBrokenDownBy"
              options={[
                { label: 'None', value: AggregationsBrokenDownByMethod.None },
                { label: 'Chopping', value: AggregationsBrokenDownByMethod.Chopping },
                { label: 'Shredding', value: AggregationsBrokenDownByMethod.Shredding },
              ]}
            />

            {!noneAggregations &&
              (mouldSizeIsOneLitre ? (
                <Field
                  input={<RadioGroup />}
                  name="Preparation.BrokenDownToPassSieveSize"
                  options={brokenDownToPassSieveSizeOptions}
                />
              ) : (
                <Field
                  input={<TextOutput />}
                  name="Preparation.BrokenDownToPassSieveSize"
                  value={brokenDownToPassSieveSizeOptions.find((o) => o.value === brokenDownToPassSieveSize)?.label}
                />
              ))}

            {!isGradingAssessmentMeasured &&
              gradingZone !== GradningZoneType.One &&
              gradingZone !== GradningZoneType.Three &&
              !gradingZoneEqualsXAndClientAgreementNo &&
              !(gradingZoneEqualsTwo && mouldSizeIsCBR) && (
                <NamePrefixModifier appendNamePrefix="Equipment">
                  <WorksheetBalanceFieldSet name="MaterialRetainedBalance" label="Balance" />
                </NamePrefixModifier>
              )}

            {!isGradingAssessmentMeasured && gradingZoneEqualsTwo && mouldSizeIsOneLitre && (
              <Field input={<NumericInput />} name="Preparation.CumulativeMassRetainedOn20mmSieve" />
            )}
            {!isGradingAssessmentMeasured &&
              (gradingZoneEqualsFourOrFive || gradingZoneEqualsXAndClientAgreementYes) && (
                <Field input={<NumericInput />} name="Preparation.CumulativeMassRetainedOn37_5mmSieve" />
              )}
            <Field input={<TextOutput />} name="Preparation.TestPortionMass" />

            {showCopyGrainSize && <Field input={<Checkbox />} name="Preparation.CopyGrainSize" />}

            {showCopyGrainSize && isCopyGrainSizeChecked ? (
              <>
                <Field
                  input={<TextOutput />}
                  name="Preparation.VisualNominalParticleSize"
                  value={
                    visualNominalParticleSizeSieveOptions.find((o) => o.value === visualNominalParticleSize)?.label
                  }
                />
                <Field
                  input={<TextOutput />}
                  name="Preparation.FineGrainSize"
                  value={grainSizeOptions.find((o) => o.value === grainSize)?.label}
                />
              </>
            ) : (
              <>
                <Field
                  input={<RadioGroup />}
                  name="Preparation.VisualNominalParticleSize"
                  options={visualNominalParticleSizeSieveOptions}
                  simpleValue
                />
                <Field input={<RadioGroup />} name="Preparation.FineGrainSize" options={grainSizeOptions} />
              </>
            )}
          </>
        )}
      </ListFieldSection>
    </>
  );
};

BsPreparationSection = withInjectedProps(BsPreparationSection);

export { BsPreparationSection };
export default BsPreparationSection;
